<template>
  <Modal :is-open="showForm" @close="$emit('cancel')">
    <template #header>
      <h2 class="text-xl font-semibold">
        {{ equipment ? 'Edit Equipment' : 'Add New Equipment' }}
      </h2>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <!-- Progress Bar -->
      <div v-if="loading" class="w-full bg-gray-200 rounded-full h-1.5 mb-4 overflow-hidden">
        <div class="bg-indigo-600 h-full animate-progress-indeterminate"></div>
      </div>

      <!-- Feedback Message handled by global notifications, keeping for local if needed -->

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Equipment Name *</label>
        <input
          v-model="formData.name"
          required
          placeholder="e.g. Tractor 4000"
          class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          :class="{ 'border-red-500': errors.name }"
        />
        <p v-if="errors.name" class="text-red-500 text-xs mt-1">{{ errors.name }}</p>
      </div>

      <!-- Type and Model -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Type *</label>
          <select
            v-model="formData.type"
            required
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            :class="{ 'border-red-500': errors.type }"
          >
            <option value="">Select Type</option>
            <option v-for="t in allowedTypes" :key="t" :value="t">{{ t }}</option>
          </select>
          <p v-if="errors.type" class="text-red-500 text-xs mt-1">{{ errors.type }}</p>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Model *</label>
          <input
            v-model="formData.model"
            required
            placeholder="e.g. JD-2024"
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            :class="{ 'border-red-500': errors.model }"
          />
          <p v-if="errors.model" class="text-red-500 text-xs mt-1">{{ errors.model }}</p>
        </div>
      </div>

      <!-- Serial Number -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Serial Number *</label>
        <input
          v-model="formData.serial_number"
          required
          placeholder="Auto-generated or Manual"
          class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          :class="{ 'border-red-500': errors.serial_number }"
          @input="isSerialNumberManuallyEdited = true"
        />
        <p v-if="errors.serial_number" class="text-red-500 text-xs mt-1">
          {{ errors.serial_number }}
        </p>
      </div>

      <!-- Fuel Requirements -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Requires Fuel?</label>
          <select
            v-model="formData.requires_fuel"
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          >
            <option :value="false">No</option>
            <option :value="true">Yes</option>
          </select>
        </div>

        <div v-if="formData.requires_fuel">
          <label class="block text-sm font-medium text-gray-700 mb-1">Fuel Type *</label>
          <select
            v-model="formData.fuel_type"
            required
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            :class="{ 'border-red-500': errors.fuel_type }"
          >
            <option value="">Select Fuel Type</option>
            <option value="Diesel">Diesel</option>
            <option value="Gasoline">Gasoline</option>
            <option value="Electric">Electric</option>
            <option value="Propane">Propane</option>
            <option value="Petrol">petrol</option>
          </select>
          <p v-if="errors.fuel_type" class="text-red-500 text-xs mt-1">{{ errors.fuel_type }}</p>
        </div>
      </div>

      <!-- Status and Purchase Date -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Status *</label>
          <select
            v-model="formData.status"
            required
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          >
            <option value="active">Active</option>
            <option value="retired">Retired</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Date *</label>
          <input
            type="date"
            v-model="formData.purchase_date"
            required
            class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
            :class="{ 'border-red-500': errors.purchase_date }"
          />
          <p v-if="errors.purchase_date" class="text-red-500 text-xs mt-1">
            {{ errors.purchase_date }}
          </p>
        </div>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <input
          v-model="formData.description"
          required
          placeholder="Brief description of the equipment"
          class="w-full p-2 border border-gray-400 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
          :class="{ 'border-red-500': errors.description }"
        />
        <p v-if="errors.description" class="text-red-500 text-xs mt-1">{{ errors.description }}</p>
      </div>

      <!-- Form Actions -->
      <div class="flex space-x-3 pt-4">
        <button type="submit" class="btn-primary flex-1" :disabled="loading">
          {{ loading ? 'Saving...' : equipment ? 'Update' : 'Create' }}
        </button>
        <button type="button" @click="handleCancel" class="btn-secondary flex-1">Cancel</button>
      </div>
    </form>

    <!-- Post-create dialog -->
    <div
      v-if="showPostCreateDialog"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50"
    >
      <div class="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
        <h3 class="text-lg font-semibold mb-4">Equipment added successfully!</h3>
        <p class="mb-6">Would you like to add another equipment or view the equipment list?</p>
        <div class="flex space-x-3">
          <button @click="handleAddAnother" class="btn-primary flex-1">Add Another</button>
          <button @click="handleViewList" class="btn-secondary flex-1">View List</button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import type { Equipment } from '@/types/equipment'
import Modal from '@/components/shared/Modal.vue'
import { equipmentApi } from '@/api/equipmentApi'
import { useLoadingStore } from '@/stores/loading'
import { useNotificationStore } from '@/stores/notification'

interface Props {
  equipment?: Equipment | null
  showForm: boolean
  loading?: boolean
}

interface Errors {
  name?: string
  type?: string
  model?: string
  serial_number?: string
  fuel_type?: string
  purchase_date?: string
  description?: string
  status?: string
  requires_fuel?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'saved'): void
}>()

type LocalForm = {
  name: string
  type: string
  model: string
  serial_number: string
  requires_fuel: boolean
  fuel_type?: string | undefined
  status: 'active' | 'inactive' | 'maintenance'
  purchase_date: string
  description: string
}

const formData = reactive<LocalForm>({
  name: '',
  type: '',
  model: '',
  serial_number: '',
  requires_fuel: false,
  fuel_type: undefined,
  status: 'active',
  purchase_date: new Date().toISOString().split('T')[0],
  description: '',
})

function hasDescription(x: unknown): x is { description?: string } {
  return !!x && typeof x === 'object' && 'description' in x
}

// Use a Record so indexing by key is allowed and values are strings or undefined
const errors = reactive<Record<keyof Errors, string | undefined>>({
  name: undefined,
  type: undefined,
  model: undefined,
  serial_number: undefined,
  fuel_type: undefined,
  purchase_date: undefined,
  description: undefined,
  status: undefined,
  requires_fuel: undefined,
})
const feedbackMessage = ref<string>('')
// Internal loading state, but allow parent to pass `loading` prop. Expose `loading` as a
// computed so template and code use a single source.
const internalLoading = ref(false)
const loading = computed<boolean>({
  get: () => (props.loading ?? internalLoading.value) as boolean,
  set: (v: boolean) => {
    internalLoading.value = v
  },
})
const showPostCreateDialog = ref(false)
const isSerialNumberManuallyEdited = ref(false)
const globalLoading = useLoadingStore()
const notificationStore = useNotificationStore()

const generateSerialNumber = () => {
  // Only autogenerate if it's a new equipment and hasn't been manually edited
  if (isSerialNumberManuallyEdited.value || props.equipment) return

  if (!formData.name.trim() || !formData.model.trim()) {
    formData.serial_number = ''
    return
  }

  // Generate prefix from name (first 3 chars, uppercase, alphanumeric only)
  const namePrefix = formData.name
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 3)
    .toUpperCase()

  // Generate prefix from model (first 3 chars, uppercase, alphanumeric only)
  const modelPrefix = formData.model
    .trim()
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 3)
    .toUpperCase()

  // Random suffix (4 chars)
  const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase()

  formData.serial_number = `${namePrefix}-${modelPrefix}-${randomSuffix}`
}

// Watch name and model for autogeneration
watch([() => formData.name, () => formData.model], generateSerialNumber)

// Populate form if editing existing equipment
watch(
  () => props.equipment,
  (equip) => {
    if (equip) {
      Object.assign(formData, {
        name: equip.name,
        type: equip.type,
        model: equip.model,
        serial_number: equip.serial_number,
        requires_fuel: equip.requires_fuel,
        fuel_type: equip.fuel_type,
        status: equip.status,
        purchase_date: equip.purchase_date,
        // Equipment type doesn't include description; use empty string as fallback
        description: hasDescription(equip) ? (equip.description ?? '') : '',
      })
    } else {
      // If modal is closed (equipment prop becomes null), reset dialog and form
      showPostCreateDialog.value = false
      isSerialNumberManuallyEdited.value = false
      Object.assign(formData, {
        name: '',
        type: '',
        model: '',
        serial_number: '',
        requires_fuel: false,
        fuel_type: undefined,
        status: 'active',
        purchase_date: new Date().toISOString().split('T')[0],
        description: '',
      })
    }
  },
  { immediate: true },
)

const allowedTypes = [
  'Farming Equipment',
  'Livestock Keeping Equipment',
  'Transport Equipment',
  'other',
]
const allowedStatuses = ['active', 'maintenance', 'retired']

const validateForm = (): boolean => {
  const newErrors: Errors = {}
  const nameRegex = /^[A-Za-z0-9 \-_]+$/
  const modelSerialRegex = /^[\w\-]+$/
  const today = new Date().toISOString().split('T')[0]

  if (!formData.name.trim()) newErrors.name = 'Name is required'
  else if (!nameRegex.test(formData.name))
    newErrors.name = 'Name can only contain letters, numbers, spaces, hyphens, and underscores'
  if (!formData.type.trim()) newErrors.type = 'Type is required'
  else if (!allowedTypes.includes(formData.type))
    newErrors.type = 'Type must be one of: ' + allowedTypes.join(', ')
  if (!formData.status.trim()) newErrors.status = 'Status is required'
  else if (!allowedStatuses.includes(formData.status))
    newErrors.status = 'Status must be one of: ' + allowedStatuses.join(', ')
  if (!formData.purchase_date) newErrors.purchase_date = 'Purchase date is required'
  else if (formData.purchase_date > today)
    newErrors.purchase_date = 'Purchase date cannot be in the future'
  if (formData.requires_fuel === undefined || formData.requires_fuel === null)
    newErrors.requires_fuel = 'Fuel requirement is required'
  if (formData.requires_fuel && (!formData.fuel_type || !formData.fuel_type.trim()))
    newErrors.fuel_type = 'Fuel type is required for fuel-consuming equipment'
  if (formData.model && !modelSerialRegex.test(formData.model))
    newErrors.model = 'Model can only contain letters, numbers, underscores, and hyphens'
  if (formData.serial_number && !modelSerialRegex.test(formData.serial_number))
    newErrors.serial_number =
      'Serial number can only contain letters, numbers, underscores, and hyphens'
  // description is optional, no validation

  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const handleSubmit = async () => {
  if (validateForm()) {
    loading.value = true
    globalLoading.start()
    let result
    try {
      if (props.equipment && props.equipment.id) {
        // Editing existing equipment
        result = await equipmentApi.update(props.equipment.id, { ...formData })
      } else {
        // Creating new equipment
        result = await equipmentApi.create({ ...formData })
      }
      
      if (result.success) {
        notificationStore.showSuccess(result.message || 'Equipment saved successfully!')
        if (!props.equipment) {
          showPostCreateDialog.value = true
        } else {
          emit('saved')
        }
        ;(Object.keys(errors) as Array<keyof Errors>).forEach((key) => {
          errors[key] = undefined
        })
      } else {
        notificationStore.showError(result.message || 'Failed to save equipment')
      }
    } catch (err) {
      console.error(err)
      notificationStore.showError('An unexpected error occurred')
    } finally {
      loading.value = false
      globalLoading.stop()
    }
  } else {
    feedbackMessage.value = 'Please fix the errors above.'
  }
}

const handleAddAnother = () => {
  showPostCreateDialog.value = false
  isSerialNumberManuallyEdited.value = false
  Object.assign(formData, {
    name: '',
    type: '',
    model: '',
    serial_number: '',
    requires_fuel: false,
    fuel_type: undefined,
    status: 'active',
    purchase_date: new Date().toISOString().split('T')[0],
    description: '',
  })
}

const handleViewList = () => {
  showPostCreateDialog.value = false
  emit('saved')
}

const handleCancel = () => {
  emit('cancel')
  feedbackMessage.value = 'Action cancelled.'
  setTimeout(() => {
    feedbackMessage.value = ''
  }, 1000)
}
</script>

<style scoped>
@keyframes progress-min {
  0% { width: 0%; }
  100% { width: 100%; }
}

.animate-progress-indeterminate {
  animation: progress-indeterminate 1.5s infinite linear;
}

@keyframes progress-indeterminate {
  0% { transform: translateX(-100%); width: 30%; }
  50% { transform: translateX(100%); width: 30%; }
  100% { transform: translateX(200%); width: 30%; }
}
</style>
